/**
 * A usage-vs-limit meter. The fill carries severity (accent → warning → critical) and the track is a
 * lighter step of the same family, so state reads across the whole bar; the number is always printed
 * beside it (colour never carries it alone).
 *
 * `percent` null means "no limit configured" or "not collected" — the caller says which in `note`; the
 * meter then draws an empty dashed track instead of a bar at 0 %.
 */
export function Meter({
  percent, label, note, warnAt = 90, criticalAt = 97,
}: {
  percent: number | null;
  label: string;
  note?: string;
  warnAt?: number;
  criticalAt?: number;
}) {
  if (percent === null || !Number.isFinite(percent)) {
    return (
      <div className="meter-row" title={note}>
        <div className="meter-bar meter-bar-empty" aria-hidden="true" />
        <span className="t-caption subtle">{note ?? 'no limit'}</span>
      </div>
    );
  }

  const clamped = Math.max(0, Math.min(100, percent));
  const tone = percent >= criticalAt ? 'critical' : percent >= warnAt ? 'warning' : 'ok';

  return (
    <div className="meter-row">
      <div
        className={`meter-bar meter-${tone}`}
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamped)}
      >
        <span style={{ width: `${Math.max(clamped, 1.5)}%` }} />
      </div>
      <span className="t-caption tabular">
        {percent.toFixed(1)}%{tone !== 'ok' && (tone === 'critical' ? ' ✕' : ' !')}
      </span>
    </div>
  );
}

/**
 * A 6-point trend for a table cell (Kafka lag `recent`). A null point is a GAP — the line breaks there
 * and a hollow tick marks it — never a drop to zero.
 */
export function Sparkline({ values, label }: { values: (number | null)[]; label: string }) {
  const width = 72;
  const height = 20;
  const pad = 2;
  const known = values.filter((v): v is number => v !== null);
  if (values.length === 0 || known.length === 0) {
    return <span className="t-caption subtle">no samples</span>;
  }

  const max = Math.max(...known);
  const min = Math.min(...known);
  const span = max - min || 1;
  const x = (i: number) => (values.length <= 1 ? width / 2 : pad + (i / (values.length - 1)) * (width - pad * 2));
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);

  const segments: string[] = [];
  let current: string[] = [];
  values.forEach((v, i) => {
    if (v === null) {
      if (current.length) segments.push(current.join(' '));
      current = [];
      return;
    }
    current.push(`${current.length ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  });
  if (current.length) segments.push(current.join(' '));

  const lastIndex = values.length - 1;
  const last = values[lastIndex];

  return (
    <svg className="sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      {segments.map((d, i) => (
        <path key={i} d={d} fill="none" stroke="var(--series-1)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {values.map((v, i) =>
        v === null ? (
          <line key={`gap-${i}`} x1={x(i)} x2={x(i)} y1={height - 5} y2={height - 1} stroke="var(--viz-muted)" strokeWidth="1" />
        ) : null,
      )}
      {last !== null && last !== undefined && (
        <circle cx={x(lastIndex)} cy={y(last)} r="2.5" fill="var(--series-1)" stroke="var(--viz-surface)" strokeWidth="1" />
      )}
    </svg>
  );
}
